import type { ReactNode } from "react";
import { cx } from "../presentation.tsx";

export interface SuccessCheckProps {
  /** CSS length; defaults to 4em so it scales with the presentation's type. */
  size?: string;
  /** Accessible text; omit when a heading next to it says what happened. */
  label?: string;
  className?: string;
}

/**
 * A check drawn in a circle: the ring fills, then the tick draws, then a soft pulse. Under
 * reduced motion it simply appears. Success colour, so the kiosk's own success green applies.
 */
export function SuccessCheck({ size = "4em", label, className }: SuccessCheckProps) {
  return (
    <svg
      className={cx("pmu-check", className)} viewBox="0 0 52 52" width={size} height={size} focusable="false"
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
    >
      <circle className="pmu-check__disc" cx="26" cy="26" r="25" />
      <path className="pmu-check__tick" d="M15 27.5l7 7 15-16" fill="none" />
    </svg>
  );
}

export interface SuccessScreenProps {
  title: ReactNode;
  description?: ReactNode;
  /** A summary of what was recorded: names, the time, a ListGroup. */
  children?: ReactNode;
  /** "Done", "Hand over another child". */
  actions?: ReactNode;
  className?: string;
}

/**
 * The end of a flow: the animated check, what happened in one line, the record, the way on.
 * Announced politely (role="status") so a screen reader hears the outcome.
 */
export function SuccessScreen({ title, description, children, actions, className }: SuccessScreenProps) {
  return (
    <section className={cx("pmu-success", className)} role="status" aria-live="polite">
      <SuccessCheck className="pmu-success__check" />
      <h2 className="pmu-success__title">{title}</h2>
      {description && <p className="pmu-success__description">{description}</p>}
      {children && <div className="pmu-success__details">{children}</div>}
      {actions && <div className="pmu-success__actions">{actions}</div>}
    </section>
  );
}
